/*
 * OKLCH color math for palette generation.
 *
 * Pure module — no DOM, no session state — so it is usable from runtime
 * theming (blink-v2 dynamic theme), charts, tests and node scripts alike.
 *
 * Holds the single copy of the sRGB↔OKLab conversion matrices; the chart
 * dark-mode remap in dark-mode-color-utils.ts imports its conversions from
 * here.
 *
 * Tone model: tones are M3-style 0 (black) … 100 (white). HCT's tone is
 * CIELAB L-star; Ottosson's "Lr" toe estimate satisfies Lr ≈ L-star / 100 for
 * the sRGB reference white, so mapping tone t → OKLab L via the toe keeps the
 * Material tone tables — and the Δtone ⇒ WCAG-contrast rules of thumb
 * (Δ40 ⇒ 3:1, Δ50 ⇒ 4.5:1) — portable to OKLCH. Contrast is still asserted
 * empirically in tests rather than trusted from the approximation.
 * Reference: https://bottosson.github.io/posts/colorpicker/#intermission---a-new-lightness-estimate-for-oklab
 */
// ---------------------------------------------------------------------------
// sRGB ↔ OKLab / OKLCH conversions (hue in radians)
// ---------------------------------------------------------------------------
export const srgbToLinear = (c) => c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
export const linearToSrgb = (c) => c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
export function hexToOklch(hex) {
    // Reject anything that isn't a full 6-digit "#RRGGBB" hex (shorthand
    // "#F00", 8-digit "#RRGGBBAA", non-hex chars). Without this, slicing a
    // short string yields NaN channels that propagate to "#NaNNaNNaN"; the
    // caller's try/catch turns this throw into a clean passthrough instead.
    if (!/^#[0-9a-fA-F]{6}$/.test(hex)) {
        throw new Error(`hexToOklch: expected a 6-digit "#RRGGBB" hex, got "${hex}"`);
    }
    const r = srgbToLinear(parseInt(hex.slice(1, 3), 16) / 255);
    const g = srgbToLinear(parseInt(hex.slice(3, 5), 16) / 255);
    const b = srgbToLinear(parseInt(hex.slice(5, 7), 16) / 255);
    // linear sRGB -> LMS
    const lPrime = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const mPrime = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const sPrime = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    // LMS -> OKLab
    const L = 0.2104542553 * lPrime + 0.793617785 * mPrime - 0.0040720468 * sPrime;
    const a = 1.9779984951 * lPrime - 2.428592205 * mPrime + 0.4505937099 * sPrime;
    const bb = 0.0259040371 * lPrime + 0.7827717662 * mPrime - 0.808675766 * sPrime;
    // OKLab -> OKLCH
    const C = Math.sqrt(a * a + bb * bb);
    const H = Math.atan2(bb, a);
    return [L, C, H];
}
/** OKLCH → linear sRGB channels, unclamped (may exceed [0,1] out of gamut). */
function oklchToLinearSrgb(L, C, H) {
    // OKLCH -> OKLab
    const a = C * Math.cos(H);
    const bb = C * Math.sin(H);
    // OKLab -> LMS
    const lPrime = L + 0.3963377774 * a + 0.2158037573 * bb;
    const mPrime = L - 0.1055613458 * a - 0.0638541728 * bb;
    const sPrime = L - 0.0894841775 * a - 1.291485548 * bb;
    const l = lPrime * lPrime * lPrime;
    const m = mPrime * mPrime * mPrime;
    const s = sPrime * sPrime * sPrime;
    // LMS -> linear sRGB
    const r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
    const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
    const b = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;
    return [r, g, b];
}
/**
 * OKLCH → "#rrggbb" with a simple per-channel clamp. Clamping can shift hue
 * for out-of-gamut inputs — palette code should prefer
 * {@link oklchToHexGamutMapped}. Kept as-is because the chart dark-mode remap
 * depends on this exact behavior.
 */
export function oklchToHex(L, C, H) {
    const [r, g, b] = oklchToLinearSrgb(L, C, H);
    const toByte = (c) => Math.round(Math.min(1, Math.max(0, linearToSrgb(c))) * 255)
        .toString(16)
        .padStart(2, '0');
    return `#${toByte(r)}${toByte(g)}${toByte(b)}`;
}
// ---------------------------------------------------------------------------
// Tone ↔ OKLab lightness (Ottosson toe)
// ---------------------------------------------------------------------------
const K1 = 0.206;
const K2 = 0.03;
const K3 = (1 + K1) / (1 + K2);
/** OKLab L (0..1) → Lr lightness estimate (0..1), Lr ≈ CIELAB L-star / 100. */
export const toe = (L) => 0.5 * (K3 * L - K1 + Math.sqrt((K3 * L - K1) * (K3 * L - K1) + 4 * K2 * K3 * L));
/** Lr (0..1) → OKLab L (0..1). Inverse of {@link toe}. */
export const toeInv = (Lr) => (Lr * Lr + K1 * Lr) / (K3 * (Lr + K2));
const clamp = (x, lo, hi) => Math.min(hi, Math.max(lo, x));
/** M3-style tone 0..100 → OKLab L. */
export const toneToOklabL = (tone) => toeInv(clamp(tone, 0, 100) / 100);
/** OKLab L → M3-style tone 0..100. */
export const oklabLToTone = (L) => 100 * toe(clamp(L, 0, 1));
// ---------------------------------------------------------------------------
// Gamut mapping by chroma reduction
// ---------------------------------------------------------------------------
// Tolerance absorbs float noise so colors exactly on the gamut boundary
// (e.g. pure #ff0000 round-tripped) still count as inside.
const GAMUT_EPSILON = 1e-4;
/** True when OKLCH(L,C,H) lands inside the sRGB gamut (pre-gamma check). */
export function isInSrgbGamut(L, C, H) {
    const [r, g, b] = oklchToLinearSrgb(L, C, H);
    return (r >= -GAMUT_EPSILON &&
        r <= 1 + GAMUT_EPSILON &&
        g >= -GAMUT_EPSILON &&
        g <= 1 + GAMUT_EPSILON &&
        b >= -GAMUT_EPSILON &&
        b <= 1 + GAMUT_EPSILON);
}
// sRGB's most chromatic color in OKLCH is ≈0.37 (pure green); 0.4 safely
// bounds the search.
const MAX_CHROMA_SEARCH_BOUND = 0.4;
/**
 * Largest chroma that stays inside sRGB at the given lightness and hue.
 * Binary search — 20 iterations gives ~4e-7 resolution, far below a
 * quantized 8-bit channel step.
 */
export function maxChromaInGamut(L, H) {
    if (L <= 0 || L >= 1)
        return 0;
    let lo = 0;
    let hi = MAX_CHROMA_SEARCH_BOUND;
    for (let i = 0; i < 20; i++) {
        const mid = (lo + hi) / 2;
        if (isInSrgbGamut(L, mid, H)) {
            lo = mid;
        }
        else {
            hi = mid;
        }
    }
    return lo;
}
/**
 * OKLCH → "#rrggbb", reducing chroma (never lightness or hue) until the color
 * fits the sRGB gamut. Unlike per-channel clamping this keeps tonal ramps
 * on-hue and on-tone — a saturated yellow at low L stays yellow-hued instead
 * of clamping toward olive.
 */
export function oklchToHexGamutMapped(L, C, H) {
    return oklchToHex(L, Math.min(C, maxChromaInGamut(L, H)), H);
}
/**
 * Build a tonal palette from a source color: constant hue, chroma held at the
 * source's (optionally capped) until the gamut forces reduction — the same
 * behavior as HCT's TonalPalette.fromHueAndChroma, so M3 tone tables port
 * unchanged. Pass `maxChroma` to build neutral (gray) ramps that keep the
 * brand hue but cannot tint surfaces visibly.
 */
export function makeTonalPalette(sourceHex, options = {}) {
    const [, sourceChroma, hue] = hexToOklch(sourceHex);
    const chroma = options.maxChroma !== undefined
        ? Math.min(sourceChroma, options.maxChroma)
        : sourceChroma;
    const cache = new Map();
    return {
        sourceHex: sourceHex.toLowerCase(),
        hue,
        chroma,
        tone(t) {
            const cached = cache.get(t);
            if (cached !== undefined)
                return cached;
            const L = toneToOklabL(t);
            const hex = oklchToHexGamutMapped(L, chroma, hue);
            cache.set(t, hex);
            return hex;
        },
    };
}
// ---------------------------------------------------------------------------
// Relative state stepping (hover / pressed)
// ---------------------------------------------------------------------------
/**
 * Shift a color by Δtone (positive = lighter), preserving chroma and hue,
 * gamut-mapped. Tone is clamped to [0, 100].
 */
export function shiftTone(hex, deltaTone) {
    const [L, C, H] = hexToOklch(hex);
    const tone = clamp(oklabLToTone(L) + deltaTone, 0, 100);
    return oklchToHexGamutMapped(toneToOklabL(tone), C, H);
}
/**
 * Tone deltas calibrated to the static radiant button ramps
 * (_tokens-component-button.scss): light brand-60→70→80 measures Δ −10.6 /
 * −25.7; dark brand-50→40→30 measures Δ +13.5 / +21.1.
 */
export const HOVER_DELTA = 11;
export const PRESSED_DELTA = 23;
// Below/above these tones there is no room left to step in the preferred
// direction; flip so hover/pressed remain distinguishable.
const DARKEN_HEADROOM_TONE = 8;
const LIGHTEN_HEADROOM_TONE = 95;
/**
 * Derive hover/pressed colors as *relative* tone steps from the actual
 * rendered base color (never absolute palette tones — an absolute tone 30
 * turns a light-yellow brand's hover dark brown). Steps darken when
 * `preferDarken`, lighten otherwise, flipping direction when the base is too
 * close to black/white to step meaningfully.
 */
export function deriveStateColors(baseHex, opts) {
    const { preferDarken, hoverDelta = HOVER_DELTA, pressedDelta = PRESSED_DELTA, } = opts;
    const [L] = hexToOklch(baseHex);
    const baseTone = oklabLToTone(L);
    let darken = preferDarken;
    if (preferDarken && baseTone - pressedDelta < DARKEN_HEADROOM_TONE) {
        darken = false;
    }
    else if (!preferDarken &&
        baseTone + pressedDelta > LIGHTEN_HEADROOM_TONE) {
        darken = true;
    }
    const sign = darken ? -1 : 1;
    return {
        hover: shiftTone(baseHex, sign * hoverDelta),
        pressed: shiftTone(baseHex, sign * pressedDelta),
    };
}
// ---------------------------------------------------------------------------
// Contrast utilities (WCAG 2.x)
// ---------------------------------------------------------------------------
/**
 * WCAG 2.x relative luminance. Reuses the 0.04045/12.92 sRGB transfer form —
 * numerically identical to the spec's 0.03928 constant for every 8-bit input.
 */
export function relativeLuminance(hex) {
    if (!/^#[0-9a-fA-F]{6}$/.test(hex)) {
        throw new Error(`relativeLuminance: expected a 6-digit "#RRGGBB" hex, got "${hex}"`);
    }
    const r = srgbToLinear(parseInt(hex.slice(1, 3), 16) / 255);
    const g = srgbToLinear(parseInt(hex.slice(3, 5), 16) / 255);
    const b = srgbToLinear(parseInt(hex.slice(5, 7), 16) / 255);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** WCAG contrast ratio, 1..21. Symmetric in its arguments. */
export function contrastRatio(hexA, hexB) {
    const ya = relativeLuminance(hexA);
    const yb = relativeLuminance(hexB);
    return (Math.max(ya, yb) + 0.05) / (Math.min(ya, yb) + 0.05);
}
const WCAG_AA_TEXT = 4.5;
/**
 * Pick a readable foreground for the given background: the light candidate
 * whenever it clears WCAG AA (4.5:1), otherwise whichever candidate has the
 * higher contrast ratio.
 */
export function pickOnColor(bgHex, candidates = {}) {
    var _a, _b;
    const light = (_a = candidates.light) !== null && _a !== void 0 ? _a : '#ffffff';
    const dark = (_b = candidates.dark) !== null && _b !== void 0 ? _b : '#000000';
    const lightRatio = contrastRatio(bgHex, light);
    if (lightRatio >= WCAG_AA_TEXT)
        return light;
    return lightRatio >= contrastRatio(bgHex, dark) ? light : dark;
}
/**
 * Euclidean distance in OKLab. ~0.02 is near the just-noticeable difference;
 * used by calibration tests to compare generated ramps against the static
 * design-system hexes.
 */
export function deltaEOk(hexA, hexB) {
    const [l1, c1, h1] = hexToOklch(hexA);
    const [l2, c2, h2] = hexToOklch(hexB);
    const dL = l1 - l2;
    // Δa² + Δb² expressed in polar form
    const dAB = c1 * c1 + c2 * c2 - 2 * c1 * c2 * Math.cos(h1 - h2);
    return Math.sqrt(dL * dL + Math.max(0, dAB));
}
//# sourceMappingURL=oklch-palette.js.map